import toast from 'react-hot-toast';
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDoc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import { db } from '../../../firebase/clientApp';
import isEqual from 'lodash/isEqual';
import { cleanData } from './utility/formatData';
import calculateField from './utility/calculateFields';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase/clientApp';
import FormUIOptions from './formElements/formUIOptions';

async function createDocument(data, pathToSource, Id = null) {
  if (!data || !pathToSource) {
    throw new Error("Can't update document: no data or path provided");
  }

  const newDocRef = doc(collection(db, pathToSource));
  const newDocId = Id || newDocRef.id;

  const updates = {
    ...data,
    createdAt: Date.now(),
    id: newDocId,
  };

  await setDoc(doc(db, pathToSource, Id || newDocId), updates);

  return newDocId;
}

async function updateDocument(data, existingData, pathToSource, docId) {
  if (!data || !pathToSource || !docId) {
    throw new Error("Can't update document: no data or path provided");
  }

  const deleteFields = [];
  const updates = {};
  let updatedExistingData = existingData;

  // delete field when the updated data is "N/A"
  Object.entries(data).forEach(([key, value]) => {
    if (value === 'N/A') {
      deleteFields.push(key);
    } else {
      updates[key] = value;
    }
  });

  if (deleteFields.length !== 0) {
    const docRef = doc(db, pathToSource, docId);

    const remove = {};
    deleteFields.forEach((field) => (remove[field] = deleteField()));
    await updateDoc(docRef, {
      ...remove,
    });

    const remainData = {};
    Object.entries(existingData).forEach(([key, value]) => {
      if (deleteFields.includes(key)) return;

      remainData[key] = value;
    });
    updatedExistingData = remainData;
  }

  await setDoc(doc(db, pathToSource, docId), {
    ...updatedExistingData,
    ...updates,
  });

  return docId;
}

function runCalculations(formSchema, updates, externalData) {
  const calculatedInputFields = [];

  formSchema?.inputs?.schema.map((section) => {
    section.schema?.map((field) => {
      if (field.type === FormUIOptions.Calculated)
        calculatedInputFields.push(field);
      if (field.type === FormUIOptions.CalculatedSection)
        field.schema?.map((subField) => {
          if (subField.type === FormUIOptions.Calculated)
            calculatedInputFields.push(subField);
        });
    });
  });

  let calculationsFlat = [];

  if (formSchema.calculations?.schema) {
    calculationsFlat = formSchema.calculations.schema
      .map((section) => {
        switch (section.sectionType) {
          case 'twoColumn':
            return [
              ...(section.leftSchema?.schema || []),
              ...(section.rightSchema?.schema || []),
            ];
          default:
            return section.schema;
        }
      })
      .flat();
  }

  const allFields = [...calculatedInputFields, ...calculationsFlat];

  const combinedDataForCalculations = { ...externalData, ...updates };

  if (allFields.length > 0) {
    return allFields.reduce((obj, field) => {
      if (field.calcFunction) {
        const result = calculateField(field, combinedDataForCalculations);
        if (!isNaN(result)) {
          // Update the data object so that all the calculations are based on the latest values
          combinedDataForCalculations[field.name] = result;

          return { ...obj, [field.name]: result };
        } else {
          return obj;
        }
      } else {
        // no calculations, just display the value of an existing field
        if (field.doNotCalculate || field.type === FormUIOptions.Calculated)
          return obj;
        const result = combinedDataForCalculations[field.name];
        return { ...obj, [field.name]: result };
      }
    }, {});
  } else {
    return {};
  }
}

export async function handleUpdate(
  // updates is all data from the form
  updates,
  pathToSource,
  docId,
  formSchema = null,
  // externalData is data from other sources, like the property data
  externalData = {},
  callback
) {
  const calculatedFields = runCalculations(formSchema, updates, externalData);

  const formName = formSchema?.inputs?.title
    .trim()
    .split(' ')
    .join('_')
    .toLowerCase();

  // Add calculation results to the data object, then remove empty fields
  const newData = cleanData({ ...updates, ...calculatedFields });

  if (docId) {
    // Get existing data
    const docRef = doc(db, pathToSource, docId);
    const document = await getDoc(docRef);
    const existingData = document.data();

    // Extract updated data
    let updates = {};

    if (existingData) {
      Object.entries(newData).forEach(([key, value]) => {
        if (
          existingData[key] ||
          existingData[key] === null ||
          existingData[key] === false
        ) {
          if (existingData[key] === value) return;

          // Deep compare arrays and objects
          if (isEqual(existingData[key], value)) return;

          updates[key] = value;
        } else {
          if (value === 'N/A') {
            return;
          } else {
            updates[key] = value;
          }
        }
      });
    } else {
      updates = newData;
    }

    if (Object.keys(updates).length === 0) return;

    return updateDocument(updates, existingData, pathToSource, docId)
      .then(() => {
        logEvent(analytics, 'update_form', {
          firebase_screen: formName,
        });
        toast.success('Form updated');
        return callback && callback();
      })
      .catch((err) => {
        logEvent(analytics, 'exception', {
          firebase_screen: formName,
          content_type: 'update_form',
          description: 'Document update error' + err.code,
          fatal: false,
        });
        console.error(err);
        return toast.error('Error updating form');
      });
  } else {
    const updates = {};
    Object.entries(newData).forEach(([key, value]) => {
      if (value === 'N/A') {
        return;
      } else {
        updates[key] = value;
      }
    });

    if (Object.keys(updates).length === 0) return;

    return createDocument(updates, pathToSource)
      .then(() => {
        logEvent(analytics, 'update_form', {
          firebase_screen: formName,
        });
        toast.success('Form updated');
        return callback && callback();
      })
      .catch((err) => {
        logEvent(analytics, 'exception', {
          firebase_screen: formName,
          content_type: 'update_form',
          description: 'Document creation error' + err.code,
          fatal: false,
        });
        console.error(err);
        return toast.error('Error updating form');
      });
  }
}

// this function is for the lead generation embed form, which saves data to local storage instead of firebase so that it can be submitted later
export async function handleUpdateEmbed(
  data,
  pathToSource,
  formSchema = {},
  externalData = {},
  callback
) {
  const { embedEmail, ...updates } = data;

  if (!embedEmail) {
    return toast.error('Email is required');
  }

  const calculatedFields = runCalculations(formSchema, updates, externalData);

  const newData = cleanData({ ...updates, ...calculatedFields });

  const updatesToSave = {};
  Object.entries(newData).forEach(([key, value]) => {
    if (value === 'N/A') {
      return;
    } else {
      updatesToSave[key] = value;
    }
  });

  if (Object.keys(updatesToSave).length === 0) return;

  const formName = formSchema?.inputs?.title
    .trim()
    .split(' ')
    .join('_')
    .toLowerCase();

  try {
    //set it in local storage to persist in the iframe
    localStorage.setItem(
      `embedData-${formName}`,
      JSON.stringify(updatesToSave)
    );
    //create a document in firebase to save the data
    await createDocument(updatesToSave, pathToSource, embedEmail.toLowerCase());
    toast.success('Form updated');
    callback && callback();
    return true;
  } catch (error) {
    logEvent(analytics, 'exception', {
      content_type: 'update_form',
      description: error,
      fatal: false,
    });
    console.error(error);
    toast.error('Error updating form');
    return false;
  }
}

async function deleteDocument(pathToSource, docId) {
  if (!pathToSource || !docId) {
    throw new Error("Can't delete document: no path provided");
  }

  await deleteDoc(doc(db, pathToSource, docId));

  return docId;
}

export async function handleDelete(pathToSource, docId, callback) {
  return deleteDocument(pathToSource, docId)
    .then(() => {
      toast.success('Item removed');
      return callback && callback();
    })
    .catch((err) => {
      console.error(err);
      return toast.error('Error removing item');
    });
}
